import { useBidHistory } from '../hooks/useBidHistory'
import React, { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { cents } from '../util/formatters'
import { usePlacementAdjustments } from '../hooks/usePlacementAdjustments'
import { Popover } from './ui/popover'
import { BidHistoryTooltipSuppressionContext } from './bid_history_tooltip_suppression'
import { UpdateReportAdTypes } from '../util/contstants'
import type { ResolvedBidLimits } from '../types/bid_limits'
import {
    calculateEffectiveBidRanges,
    effectiveBidCents,
    formatBidStrategy,
    formatPlacementAdjustment,
} from '../util/placement_adjustments'

interface BidHistoryTooltipProps {
    children: JSX.Element
    id: number | string
    type: number
    campaign_id?: number | string
    bid?: number
    bidLimits?: ResolvedBidLimits | null
    bid_strategy?: string | null
    bid_adjustment_top_of_search?: number | null
    bid_adjustment_rest_of_search?: number | null
    bid_adjustment_home_page?: number | null
    bid_adjustment_product_page?: number | null
    ad_group_default_bid?: number | null
}

/** "$0.15 – $2.00" style range, with a dash for an unset side. */
const renderBidLimits = (limits: ResolvedBidLimits): JSX.Element | null => {
    const hasAny = limits.min.value != null || limits.max.value != null
    if (!hasAny) {
        return null
    }
    const fmt = (v: number | null) => (v == null ? '—' : cents(v))
    const inherited = limits.min.source !== 'self' && limits.max.source !== 'self'
    return (
        <div className="mt-1">
            Bid limits: {fmt(limits.min.value)} – {fmt(limits.max.value)}
            {inherited && <span> (inherited)</span>}
        </div>
    )
}

export const BidHistoryTooltip = ({
    children,
    id,
    type,
    campaign_id,
    bid,
    bidLimits,
    bid_strategy,
    bid_adjustment_top_of_search,
    bid_adjustment_rest_of_search,
    bid_adjustment_home_page,
    bid_adjustment_product_page,
    ad_group_default_bid,
}: BidHistoryTooltipProps): JSX.Element => {
    const { loading: historyLoading, history, load: loadBidHistory } = useBidHistory(type)
    const [suppressed, setSuppressed] = useState(false)
    const [isOpen, setIsOpen] = useState(false)

    const {
        loading: placementsLoading,
        placements,
        load: loadPlacements,
    } = usePlacementAdjustments()

    // `undefined` means an older row shape; null is an explicit, unmodelled row value.
    const hasRowBidSettings = [
        bid_strategy,
        bid_adjustment_top_of_search,
        bid_adjustment_rest_of_search,
        bid_adjustment_home_page,
        bid_adjustment_product_page,
    ].every((value) => value !== undefined)
    const effectiveBidRanges = calculateEffectiveBidRanges({
        baseBidCents: bid,
        adGroupDefaultBidCents: ad_group_default_bid,
        strategy: bid_strategy,
        adjustments: {
            topOfSearch: bid_adjustment_top_of_search,
            restOfSearch: bid_adjustment_rest_of_search,
            // Home page is Sponsored-Brands-only, hidden for now; the backend still returns it.
            productPage: bid_adjustment_product_page,
        },
    })

    const name = useMemo(() => {
        switch (type) {
            case UpdateReportAdTypes.Campaigns:
                return 'budget'
            default:
                return 'bid'
        }
    }, [type])

    useEffect(() => {
        if (suppressed) {
            setIsOpen(false)
        }
    }, [suppressed])

    return (
        <BidHistoryTooltipSuppressionContext.Provider value={setSuppressed}>
            <Popover
                position="left"
                interactionKind="hover"
                disabled={suppressed}
                isOpen={isOpen}
                onOpening={() => {
                    loadBidHistory(id)
                    if (!hasRowBidSettings && campaign_id) {
                        loadPlacements(campaign_id)
                    }
                }}
                onOpened={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                content={
                    <div className="p-2">
                        {bidLimits && renderBidLimits(bidLimits)}
                        {hasRowBidSettings && !effectiveBidRanges && (
                            <div className="text-gray-600">Effective bid: Not applicable</div>
                        )}
                        {hasRowBidSettings && effectiveBidRanges && (
                            <div className="text-gray-700">
                                <div className="flex justify-between gap-4 py-1">
                                    <span>Bid strategy</span>
                                    <span>{formatBidStrategy(bid_strategy)}</span>
                                </div>
                                <div className="mt-1 border-t border-gray-200 pt-1">
                                    {Object.entries(effectiveBidRanges.placements)
                                        .filter(([placement]) => placement !== 'homePage')
                                        .map(([placement, range]) => (
                                            <div
                                                key={placement}
                                                className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 py-1"
                                            >
                                                <span>
                                                    {placement === 'topOfSearch'
                                                        ? 'Top of search'
                                                        : placement === 'restOfSearch'
                                                          ? 'Rest of search'
                                                          : 'Product page'}
                                                </span>
                                                <span className="tabular-nums">
                                                    {formatPlacementAdjustment(
                                                        range.adjustmentPercent,
                                                    )}
                                                </span>
                                                <span className="tabular-nums">
                                                    {bid_strategy === 'NONE'
                                                        ? `Fixed bids: ${cents(range.ceilingCents)}`
                                                        : `${cents(range.floorCents)} – ${cents(range.ceilingCents)}`}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                        {!hasRowBidSettings &&
                            !placementsLoading &&
                            placements.length > 0 &&
                            bid && (
                                <div className="text-gray-700">
                                    <div>This item belongs to a campaign with bid adjustments.</div>
                                    <div className="mt-1 border-t border-gray-200 pt-1">
                                        {placements
                                            .filter((p) => p.placement_string !== 'Home page')
                                            .map((p, i) => (
                                                <div key={i} className="py-1">
                                                    {p.placement_string}:{' '}
                                                    {formatPlacementAdjustment(p.percentage)} bid
                                                    adjustment - effective bid of{' '}
                                                    {cents(effectiveBidCents(bid, p.percentage))}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}
                        <div className="mt-1 min-h-[1.75rem] border-t border-gray-200 pt-1 text-gray-600">
                            {historyLoading ? (
                                <>Loading {name} history…</>
                            ) : history.length > 0 ? (
                                <>
                                    {history.map((h) => (
                                        <div key={h.timestamp + ':' + h.hash} className="py-1">
                                            {format(new Date(h.timestamp), 'M/d/yy H:mm a')}
                                            &nbsp; Changed {name} from {cents(h.previous_value_int)}{' '}
                                            to {cents(h.new_value_int)}
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <>No {name} changes to show</>
                            )}
                        </div>
                    </div>
                }
            >
                {children}
            </Popover>
        </BidHistoryTooltipSuppressionContext.Provider>
    )
}
